import { useState, useEffect } from "react";
import { Card, Input, Table } from "antd";
import { useTranslation } from "react-i18next";
import StandardButton from '../components/StandardButton';
import { toast } from "react-hot-toast";
import api from "../interceptors/api";
import ProtectedToolWrapper from '../components/ProtectedToolWrapper';

export default function FileToLink() {
  const { t } = useTranslation();
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [resultUrl, setResultUrl] = useState("");
  const [listLoading, setListLoading] = useState(false);
  const [files, setFiles] = useState([]);

  const loadFiles = async () => {
    try {
      const userProfileData = JSON.parse(localStorage.getItem('userProfileData') || 'null');
      if (!userProfileData?.id) return;

      setListLoading(true);
      const res = await api.get("/file-to-link/list");
      setFiles(res.data.files || []);
    } catch (e) {
      toast.error(t("api_request_failed"));
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => {
    loadFiles();
  }, []);

  const onUpload = async () => {
    const userProfileData = JSON.parse(localStorage.getItem('userProfileData') || 'null');
    if (!userProfileData?.id) {
      toast.error('Please log in to use this feature');
      return;
    }

    if (!file) {
      return toast.error(t("please_select_a_file"));
    }
    setUploading(true);
    setResultUrl("");

    const toastId = toast.loading(t("uploading"));

    try {
      const formData = new FormData();
      formData.append("file", file);

      const res = await api.post("/file-to-link/upload", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      setResultUrl(res.data.url);
      toast.success(t("upload_success"), { id: toastId });
      setFile(null);
      await loadFiles();
    } catch (err) {
      toast.error(err.response?.data?.error || t("api_request_failed"), {
        id: toastId,
      });
    } finally {
      setUploading(false);
    }
  };

  const handleCopyUrl = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success(t("copied_to_clipboard"));
    } catch (err) {
      const textArea = document.createElement("textarea");
      textArea.value = url;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      toast.success(t("copied_to_clipboard"));
    }
  };

  const columns = [
    { title: t("filename"), dataIndex: "filename", key: "filename" },
    { 
      title: t("size_label"), 
      dataIndex: "size_bytes", 
      key: "size_bytes", 
      width: 140,
      render: (size) => {
        if (!size) return '-';
        const sizeMB = (size / (1024 * 1024)).toFixed(2);
        return `${sizeMB} MB`;
      }
    },
    { title: t("type_label"), dataIndex: "mime_type", key: "mime_type", width: 220 },
    {
      title: t("actions"),
      key: "actions",
      width: 180,
      render: (_, record) => (
        <div className="flex space-x-2">
          <StandardButton
            type="secondary"
            size="small"
            href={record.url}
            target="_blank"
            rel="noreferrer"
            className="flex items-center"
          >
            {t("open")}
          </StandardButton>
          <StandardButton
            type="secondary"
            size="small"
            onClick={() => handleCopyUrl(record.url)}
            className="flex items-center"
          >
            {t("copy")}
          </StandardButton>
        </div>
      ),
    },
  ];

  return (
    <ProtectedToolWrapper>
      <div className="max-w-5xl mx-auto">
        <Card className="p-6 rounded-xl shadow-lg space-y-5">
          <div className="flex items-center space-x-3">
            <Input
              type="file"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              style={{ width: "90%" }}
            />
            <StandardButton 
              type="secondary" 
              onClick={onUpload} 
              loading={uploading} 
              disabled={!file}
            >
              {t("upload")}
            </StandardButton>
          </div>

          {resultUrl && (
            <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded">
              <div className="text-sm font-semibold text-green-700 mb-1">
                {t("upload_success")}!
              </div>
              <div className="flex items-center space-x-2">
                <Input
                  value={resultUrl}
                  readOnly
                  className="flex-1"
                />
                <StandardButton
                  type="secondary"
                  size="small"
                  onClick={() => handleCopyUrl(resultUrl)}
                >
                  {t("copy")}
                </StandardButton>
              </div>
            </div>
          )}

          <div className="mt-8">
            <div className="text-sm font-semibold mb-3">{t("all_files")}</div>
            <Table
              rowKey="id"
              dataSource={files}
              columns={columns}
              loading={listLoading}
              pagination={{ pageSize: 10 }}
            />
          </div>
        </Card>
      </div>
    </ProtectedToolWrapper>
  );
}